import React, { Fragment, createContext, memo, useCallback, useContext, useEffect, useState } from 'react';
import { ProductInfo } from '../types';
import { DatePicker, Divider, Form, Input, InputNumber, Radio, Select, Space, Tabs, Button, Tag, Checkbox, Card, Row, Col, FormInstance, Alert, Spin, Typography } from 'antd';
import { IDeviceDetail } from '@/api/device';
import { uniqBy } from 'lodash';
import dayjs from 'dayjs';
import { WebSocketProvider, WebSocketContext } from '@/components/WebSocket';
import { v4 } from 'uuid';
import WebSocketClass from '@/utils/web-socket';

let tabWs: WebSocketClass;
type SpecBaseType = {
  code: string;
  name: string;
  access_mode?: 'RW' | 'R';
};

type SpecSingleType =
  | {
      type: 'int' | 'float';
      specs: {
        min: number;
        max: number;
        step: number;
        unit: string;
        unitName: string;
      };
    }
  | { type: 'text'; specs: { length: number } }
  | {
      type: 'enum' | 'bool';
      specs: {
        [key: string]: string;
      };
    }
  | {
      type: 'date';
      specs: Record<string, any>;
    };

type SpecStructType = {
  type: 'struct';
  specs: (SpecBaseType & { type_spec: SpecSingleType })[];
};

type SpecArrayType = {
  type: 'array';
  specs: { size: number } & (SpecSingleType | SpecStructType);
};

type SpecMainType = SpecBaseType & { type_spec: SpecSingleType | SpecStructType | SpecArrayType };

type DataListType = { properties: SpecMainType[] } & Record<'events' | 'actions', (SpecBaseType & { list: SpecMainType[] })[]>;

const typeJson = {
  /** 登录 */
  login: { title: '登录', dataKey: '', isCommon: true },
  /** 属性上报 */
  propertiesReporting: {
    title: '属性上报',
    dataKey: 'data',
    sortData: ({ codes, data }: { codes: string[]; data: Record<string, unknown> }) => {
      return Object.fromEntries(codes.map((key) => [key, data?.[key]]));
    },
  },
  /** 事件上报 */
  eventsReporting: {
    title: '事件上报',
    dataKey: 'data',
    sortData: ({ code, data }: { code: string; data: unknown }) => {
      return { [code]: data };
    },
  },
  /** 属性获取 */
  propertiesSettings: {
    title: '属性设置',
    dataKey: 'data',
    sortData: ({ codes, data }: { codes: string[]; data: Record<string, unknown> }) => {
      return Object.fromEntries(codes.map((key) => [key, data?.[key]]));
    },
  },
  /** 服务调用 */
  actionsCall: {
    title: '服务调用',
    dataKey: 'data',
    sortData: ({ code, data }: { code: string; data: unknown }) => {
      return { [code]: data };
    },
  },
  /** 服务调用 */
  propertiesGetting: {
    title: '属性获取',
    dataKey: 'codes',
    sortData: ({ codes }: { codes: string[]; data: unknown }) => {
      return { codes };
    },
  },
  /** 注销 */
  destroy: { title: '注销', dataKey: '', isCommon: true },
};

type eventType = keyof typeof typeJson;

type BaseTabsItem = {
  key: Exclude<eventType, 'login' | 'destroy'>;
  label: string;
  type: 'device' | 'application';
  dataKey: keyof DataListType;
  isShowCheckBox?: boolean;
  isOnlyRenderLabel?: boolean;
  filterData?: (
    params:
      | SpecMainType
      | (SpecBaseType & {
          list: SpecMainType[];
        }),
  ) => boolean;
};

const tabsList: BaseTabsItem[] = [
  { key: 'propertiesReporting', label: '属性上报', type: 'device', dataKey: 'properties', isShowCheckBox: true },
  { key: 'eventsReporting', label: '事件上报', type: 'device', dataKey: 'events' },
  { key: 'propertiesSettings', label: '属性设置', type: 'application', dataKey: 'properties', isShowCheckBox: true, filterData: ({ access_mode }) => access_mode === 'RW' },
  { key: 'propertiesGetting', label: '属性获取', type: 'application', dataKey: 'properties', isShowCheckBox: true, isOnlyRenderLabel: true },
  { key: 'actionsCall', label: '服务调用', type: 'application', dataKey: 'actions' },
];

const useSendMsg = ({ detail, loadDetail }: { detail?: IDeviceDetail; loadDetail: () => void }) => {
  const { ws } = useContext(WebSocketContext);
  const [msgList, setMsgList] = useState<{ type: eventType; msg: string }[]>([]);

  const [isRunning, setIsRunning] = useState<boolean>(false);

  const sendMsg = useCallback(
    async (type: eventType, data?: Record<string, unknown>, isToClose?: boolean) => {
      try {
        const toData = {
          code: 40001,
          data: {
            title: typeJson[type].title,
            message: JSON.stringify({
              deviceId: detail?.id, // 设备id
              data: {
                msgId: v4(), // 前端生成一个唯一数或者随机码
                version: '1.0', // 版本固定为1.0
                ...data,
              },
            }),
            timestamp: dayjs().format('YYYY-MM-DD hh:mm:ss'),
          },
        };
        // console.log(toData, ws || tabWs);

        const res = await (ws || tabWs)?.send(toData);
        if (isToClose) (ws || tabWs)?.close?.();
        if (['login', 'destroy'].includes(type)) {
          loadDetail?.();
        }
        if (type === 'login') setIsRunning(true);
        if (type === 'destroy') setIsRunning(false);
        if (JSON.stringify(toData.data) !== '{}') setMsgList((pre) => [...pre, { type, msg: JSON.stringify(toData.data) }, { type, msg: JSON.stringify(res?.data?.result || {}) }]);
      } catch (e) {
        setIsRunning(false);
        // console.log(e);
      }
    },
    [ws, detail],
  );

  const clearMsgList = useCallback((key: eventType) => {
    // @ts-ignore
    setMsgList((pre) => pre.filter((item) => !['login', 'destroy', key].includes(item.type)));
  }, []);

  return { msgList, sendMsg, clearMsgList, isRunning, ws };
};

// @ts-ignore
const SendMsgContext = createContext<ReturnType<typeof useSendMsg>>(null);

const SendMsgContextProvider: React.FC<{
  children: React.ReactNode;
  detail?: IDeviceDetail;
  loadDetail: () => void;
}> = (props) => {
  const value = useSendMsg({ detail: props.detail, loadDetail: props.loadDetail });
  return <SendMsgContext.Provider value={value}>{props.children}</SendMsgContext.Provider>;
};

const FormItems = memo(
  ({
    list,
    prefix,
    nameList,
    labelPlus,
    labelBeforeRender,
    isOnlyRenderLabel,
  }: {
    list?: SpecMainType[];
    prefix: string;
    nameList?: (string | number)[];
    labelPlus?: React.ReactNode;
    isFirst?: boolean;
    isOnlyRenderLabel?: boolean;
    labelBeforeRender?: (code: string) => React.ReactNode;
  }) => {
    const renderFormItem = ({ type, specs }: SpecSingleType | SpecStructType | SpecArrayType, nameList: (string | number)[]) => {
      switch (type) {
        case 'int':
          return (
            <InputNumber
              min={specs.min}
              max={specs.max}
              step={specs.step}
              addonAfter={
                specs.unitName === '无' || !specs.unitName ? null : (
                  <>
                    {specs.unitName}/{specs.unit}
                  </>
                )
              }
              placeholder={`${specs.min}-${specs.max}`}
              style={{ width: '100%' }}
            ></InputNumber>
          );

        case 'float':
          return (
            <InputNumber
              min={specs.min}
              max={specs.max}
              step={specs.step}
              addonAfter={
                specs.unitName === '无' || !specs.unitName ? null : (
                  <>
                    {specs.unitName}/{specs.unit}
                  </>
                )
              }
              placeholder={`${specs.min}-${specs.max}`}
              style={{ width: '100%' }}
            ></InputNumber>
          );
        case 'text':
          return <Input.TextArea maxLength={specs.length} placeholder="请输入" showCount></Input.TextArea>;
        case 'bool':
          return (
            <Radio.Group
              key={nameList.toString()}
              optionType="button"
              options={Object.entries(specs).map(([value, label]) => ({ label: `${Number(value) ? 'true' : 'false'}-${label}`, value: Number(value) }))}
            ></Radio.Group>
          );
        case 'enum':
          return <Select style={{ width: '100%' }} placeholder="请选择" options={Object.entries(specs).map(([value, label]) => ({ label, value }))}></Select>;
        case 'date':
          return <DatePicker format="YYYY-MM-DD HH:mm:ss" showTime placeholder="请选择" style={{ width: '100%' }}></DatePicker>;
        case 'struct':
          return (
            <div style={{ paddingLeft: '20px' }}>
              <FormItems list={specs} prefix={`${prefix}`} nameList={nameList}></FormItems>
            </div>
          );
        case 'array':
          // eslint-disable-next-line no-case-declarations
          const { size, ...rest } = specs;
          return (
            <Form.List name={nameList.length === 1 ? nameList[0] : nameList}>
              {(fields, { add, remove }) => {
                return (
                  <div style={{ marginLeft: '20px' }}>
                    <Space direction="vertical" style={{ width: '100%' }} split={<Divider style={{ margin: '12px 0 6px' }} />}>
                      {fields.map((item, index) => (
                        <div key={item.name}>
                          <FormItems
                            labelPlus={
                              <Button
                                onClick={() => {
                                  remove(item.name);
                                }}
                                size="small"
                                type="link"
                              >
                                删除
                              </Button>
                            }
                            nameList={[item.name]}
                            prefix={prefix}
                            key={item.name}
                            list={[{ type_spec: rest, code: '', name: `元素${index + 1}` }]}
                          ></FormItems>
                        </div>
                      ))}
                      {fields.length !== size && (
                        <Button
                          onClick={() => {
                            add(undefined);
                          }}
                          type="primary"
                        >
                          添加元素
                        </Button>
                      )}
                    </Space>
                  </div>
                );
              }}
            </Form.List>
          );
        // return <></>;
        default:
          break;
      }
    };
    return (
      <Space direction="vertical" split={isOnlyRenderLabel ? null : <Divider style={{ margin: '12px 0 6px' }} />} style={{ width: '100%' }}>
        {list?.map(({ code, name, type_spec }) => {
          const newNameList = code || typeof code === 'number' ? [...(nameList || []), code] : [...(nameList || [])];
          // console.log({ code, name, type_spec });
          if (!type_spec) {
            return <Fragment key={`${prefix}_${code}_${newNameList.toString()}_Fragment`}></Fragment>;
          }
          if (isOnlyRenderLabel)
            return (
              <div style={{ padding: '10px', border: '1px solid #eee' }} key={`${prefix}_${code}_${newNameList.toString()}_onlyLabel`}>
                <Space>
                  {labelBeforeRender?.(code)}
                  <span>{name}</span>
                  {code && <span>({code})</span>}
                  <Tag>{type_spec.type}</Tag>
                  {labelPlus}
                </Space>
              </div>
            );
          return (
            <div key={`${prefix}_${code}_${newNameList.toString()}_formItem`}>
              <Form.Item
                label={
                  <Space>
                    {labelBeforeRender?.(code)}
                    <span>{name}</span>
                    {code && <span>({code})</span>}
                    <Tag>{type_spec.type}</Tag>
                    {labelPlus}
                  </Space>
                }
                {...(type_spec.type === 'date'
                  ? {
                      getValueFromEvent: (e) => (e ? dayjs(e).valueOf() : undefined),
                      getValueProps: (v) => ({ value: v ? dayjs(v) : v }),
                    }
                  : {})}
                style={{ marginBottom: 0 }}
                key={`${prefix}_${code}_${newNameList.toString()}_formItem1`}
                {...(['array', 'struct'].includes(type_spec.type) ? {} : { name: newNameList })}
              >
                {renderFormItem(type_spec, newNameList)}
              </Form.Item>
            </div>
          );
        })}
      </Space>
    );
  },
);

const CheckboxGroup = ({ children, isShowCheckBox, list }: { children: React.ReactNode; isShowCheckBox?: boolean; list: string[] }) => {
  if (isShowCheckBox)
    return (
      <Fragment key="checkbox">
        <Form.Item style={{ marginBottom: '10px' }} shouldUpdate={({ codes: preCodes }, { codes: nextCodes }) => nextCodes?.length !== preCodes?.length}>
          {({ getFieldValue, setFieldValue }) => {
            const codes = getFieldValue('codes');
            const checked = !!codes?.length && !!list.length && list.length === codes.length;
            const indeterminate = !!codes?.length && codes.length < list.length;
            return (
              <Checkbox
                checked={checked}
                indeterminate={indeterminate}
                onChange={() => {
                  setFieldValue('codes', checked ? [] : list || []);
                }}
              >
                全选
              </Checkbox>
            );
          }}
        </Form.Item>
        <Form.Item name="codes">
          <Checkbox.Group style={{ width: '100%' }}>{children}</Checkbox.Group>
        </Form.Item>
      </Fragment>
    );
  return <Fragment key="noCheckbox">{children}</Fragment>;
};

const StartEndButton = ({ isShowEnd }: { isShowEnd?: boolean }) => {
  const { sendMsg, isRunning } = useContext(SendMsgContext);

  const startSocket = async () => {
    await sendMsg('login');
  };

  const endSocket = async () => {
    await sendMsg('destroy');
  };

  if (isShowEnd && !isRunning) return <></>;

  return (
    <Button
      onClick={() => {
        !isRunning ? startSocket() : endSocket();
      }}
    >
      {!isRunning ? '启动调试' : '终止调试'}
    </Button>
  );
};

const CardOuter = ({ form, children, isShowCheckBox, cardKey }: { form: FormInstance<any>; children: React.ReactNode; isShowCheckBox?: boolean; cardKey: eventType }) => {
  const codes = Form.useWatch('codes', form);
  const code = Form.useWatch('code', form);
  const onValuesChange = () => {};
  const { sendMsg, msgList, clearMsgList, isRunning } = useContext(SendMsgContext);

  return (
    <Card styles={{ body: { padding: 0 } }}>
      <Card.Grid style={{ width: '400px', padding: 0, border: '0' }} hoverable={false}>
        <Spin spinning={!isRunning} indicator={<></>} tip={<StartEndButton />}>
          <Card
            bordered={false}
            styles={{ body: { maxHeight: '600px', height: '600px', display: 'block', position: 'relative', padding: 0, overflowY: 'hidden', paddingBottom: '40px', borderRight: '1px solid #e7eaef' } }}
          >
            <div style={{ padding: '10px 20px ', zIndex: 99, position: 'absolute', width: '100%', bottom: 0, background: '#fafafa' }}>
              <Row justify={'space-between'}>
                <Col>
                  <Button
                    onClick={() => {
                      form.resetFields();
                    }}
                  >
                    重置
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    disabled={isShowCheckBox ? !codes?.length : !code}
                    onClick={() => {
                      const values = form.getFieldsValue();
                      // @ts-ignore
                      const { dataKey, sortData } = typeJson[cardKey];
                      sendMsg(cardKey, { data: sortData?.(values) || values[dataKey] });
                    }}
                  >
                    提交
                  </Button>
                </Col>
              </Row>
            </div>
            <div style={{ padding: '20px', maxHeight: 'calc(100% - 20px)', overflowY: 'auto' }}>
              <Form form={form} layout="vertical" onValuesChange={onValuesChange} preserve>
                {children}
              </Form>
            </div>
          </Card>
        </Spin>
      </Card.Grid>
      <Card.Grid hoverable={false} style={{ width: 'calc(100% - 400px)', padding: 0 }}>
        <Card
          title="调试日志"
          extra={
            <Button
              onClick={() => {
                clearMsgList(cardKey);
              }}
            >
              清空日志
            </Button>
          }
          bordered={false}
          style={{ height: '600px', background: '#fafafa' }}
          bodyStyle={{ height: '540px', display: 'block', position: 'relative', padding: 20, overflowY: 'auto' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {msgList
              // @ts-ignore
              ?.filter((item) => item.type === cardKey || typeJson[item.type]?.isCommon)
              ?.map((item, index) => <Alert key={index} message={<Typography.Paragraph copyable={{ text: item.msg, tooltips: false }}>{item.msg}</Typography.Paragraph>} type="info" />)}
          </Space>
        </Card>
      </Card.Grid>
    </Card>
  );
};

const Outer = ({
  dataKey,
  list = [],
  prefix,
  isShowCheckBox,
  isOnlyRenderLabel,
  cardKey,
}: {
  dataKey: string;
  list?: DataListType[keyof DataListType];
  prefix: string;
  isShowCheckBox?: boolean;
  isOnlyRenderLabel?: boolean;
  cardKey: eventType;
}) => {
  const [form] = Form.useForm();

  const labelBeforeRender = (code: string) => {
    if (!isShowCheckBox) return <></>;
    return <Checkbox value={code}></Checkbox>;
  };

  if (dataKey !== 'properties') {
    return (
      <CardOuter form={form} cardKey={cardKey}>
        <>
          <Form.Item label="选择" name="code" required>
            <Select placeholder="请选择" options={list as DataListType[Exclude<keyof DataListType, 'properties'>]} fieldNames={{ label: 'name', value: 'code' }}></Select>
          </Form.Item>
          <CheckboxGroup isShowCheckBox={isShowCheckBox} list={list?.map((item) => item.code) || []}>
            <Form.Item dependencies={['code']}>
              {({ getFieldValue }) => {
                const code = getFieldValue('code') as string;
                const { list: chosenList } = (list as DataListType[Exclude<keyof DataListType, 'properties'>]).find((item) => item.code === code) || {};

                return <FormItems isOnlyRenderLabel={isOnlyRenderLabel} labelBeforeRender={labelBeforeRender} isFirst nameList={['data']} list={chosenList || []} prefix={prefix}></FormItems>;
              }}
            </Form.Item>
          </CheckboxGroup>
        </>
      </CardOuter>
    );
  }
  return (
    <CardOuter form={form} isShowCheckBox={isShowCheckBox} cardKey={cardKey}>
      <CheckboxGroup list={list?.map((item) => item.code) || []} isShowCheckBox={isShowCheckBox}>
        <FormItems isOnlyRenderLabel={isOnlyRenderLabel} labelBeforeRender={labelBeforeRender} isFirst={true} list={list as SpecMainType[]} prefix={prefix} nameList={['data']}></FormItems>
      </CheckboxGroup>
    </CardOuter>
  );
};

const { device: deviceList, application: applicationList } = tabsList.reduce(
  (pre, item) => {
    pre[item.type].push({ ...item });
    return pre;
  },
  { device: [], application: [] } as { [key: string]: BaseTabsItem[] },
);

const TabsOuter = ({ items, dataList }: { items: BaseTabsItem[]; dataList: Partial<DataListType> }) => {
  // @ts-ignore
  return (
    <Tabs
      items={items.map(({ key, label, dataKey, isShowCheckBox, isOnlyRenderLabel, filterData }) => {
        return {
          key,
          label,
          children: (
            <Outer
              cardKey={key}
              // @ts-ignore
              list={(dataList[dataKey] || []).filter((item) => (filterData ? filterData(item) : true))}
              isShowCheckBox={isShowCheckBox}
              isOnlyRenderLabel={isOnlyRenderLabel}
              dataKey={dataKey}
              prefix={key}
            ></Outer>
          ),
        };
      })}
      style={{ background: '#fff' }}
    ></Tabs>
  );
};

const Tab5 = memo(({ productInfo }: { productInfo?: ProductInfo }) => {
  const [dataList, setDataList] = useState<Partial<DataListType>>({});

  // const { initialSocket } = useSocketIo();
  // const { ws } = useContext(WebSocketContext);

  useEffect(() => {
    if (productInfo) {
      try {
        const { properties, events, actions } = productInfo;
        setDataList({
          properties: uniqBy(
            properties?.map(({ code, name, access_mode, type_spec: { type, specs } }) => ({ code, name, access_mode, type_spec: { type, specs: JSON.parse(specs) } })) || [],
            'code',
          ) as SpecMainType[],
          events: events.map(({ code, name, output_params }) => ({
            code,
            name,
            list: uniqBy(
              output_params.map(({ type_spec: { type, specs }, ...rest }) => ({ ...rest, type_spec: { type, specs: JSON.parse(specs) } })),
              'code',
            ),
          })),
          actions: actions.map(({ code, name, input_params }) => ({
            code,
            name,
            list: uniqBy(
              input_params.map(({ type_spec: { type, specs }, ...rest }) => ({ ...rest, type_spec: { type, specs: JSON.parse(specs) } })),
              'code',
            ),
          })),
        });
      } catch {}
    }
  }, [productInfo]);

  const { sendMsg, ws } = useContext(SendMsgContext);
  useEffect(() => {
    if (ws) tabWs = ws;
    // console.log(ws, tabWs);
  }, [ws]);

  useEffect(() => {
    return () => {
      // console.log(tabWs);
      sendMsg('destroy', {}, true);
    };
  }, []);
  return (
    <>
      <Tabs
        items={[
          { label: '设备模拟器', key: 'device', children: <TabsOuter items={deviceList} {...{ dataList }}></TabsOuter> },
          {
            label: '应用模拟器',
            key: 'application',
            children: <TabsOuter items={applicationList} {...{ dataList }}></TabsOuter>,
          },
        ]}
        style={{ padding: 0 }}
        tabBarStyle={{ padding: 0 }}
        tabBarExtraContent={<StartEndButton isShowEnd />}
      ></Tabs>
    </>
  );
});

const SocketTab5 = (props: { detail?: IDeviceDetail; productInfo?: ProductInfo; loadDetail: () => void }) => {
  return (
    <WebSocketProvider>
      <SendMsgContextProvider detail={props.detail} loadDetail={props.loadDetail}>
        <Tab5 {...props}></Tab5>
      </SendMsgContextProvider>
    </WebSocketProvider>
  );
};
export default SocketTab5;
